package com.iregained.site.components.sections.clinics.sections

import androidx.compose.runtime.Composable
import com.iregained.site.components.sections.clinics.styles.ClinicsHeroOverlayStyle
import com.iregained.site.components.sections.clinics.styles.ClinicsHeroStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.url
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS: Separated Background Drift and Elegant Text Glide
val ClinicsBackgroundDriftKeyframes = Keyframes {
    0.percent { Modifier.scale(1f) }
    100.percent { Modifier.scale(1.05f) }
}

val ClinicsTextGlideKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY(30.px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

@Composable
fun ClinicsHeroSection(data: ClinicsLocatorData = defaultClinicsData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    // Parent container: Static, handles dimensions and hides overflow
    Box(
        modifier = ClinicsHeroStyle.toModifier().id(data.heroSectionId ?: "clinics-hero-section")
            .styleModifier { property("scroll-margin-top", "100px") }.overflow(Overflow.Hidden)
            .position(Position.Relative), contentAlignment = Alignment.Center
    ) {
        // LAYER 1: Background Image
        // Animated independently so it doesn't distort the text
        Box(
            modifier = Modifier.fillMaxSize().position(Position.Absolute).zIndex(0)
                .backgroundImage(url(data.heroBackgroundImage)).styleModifier {
                    property("background-size", "cover")
                    property("background-position", "center")
                }.animation(
                    ClinicsBackgroundDriftKeyframes.toAnimation(
                        duration = 20.s, // Very slow, premium drift
                        timingFunction = AnimationTimingFunction.Linear, fillMode = AnimationFillMode.Forwards
                    )
                )
        )

        // LAYER 2: Dark Overlay
        Box(modifier = ClinicsHeroOverlayStyle.toModifier().zIndex(1)) {

            // LAYER 3: Content Container
            // Glides up cleanly without being shrunk by the background
            Column(
                horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.zIndex(2).animation(
                    ClinicsTextGlideKeyframes.toAnimation(
                        duration = 1.2.s,
                        timingFunction = AnimationTimingFunction.EaseOut,
                        fillMode = AnimationFillMode.Forwards
                    )
                )
            ) {
                SectionHeader(
                    text = data.headline,
                    fontSize = if (isDesktop) 48.px else 36.px,
                    modifier = Modifier.margin(bottom = 16.px).color(Colors.White),
                    alignment = Alignment.CenterHorizontally,
                    textColor = Colors.White,
                    lineColor = Colors.White
                )

                if (data.subtitle.isNotBlank()) {
                    Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(20.px)
                            .fontWeight(FontWeight.Medium).color(Colors.White).margin(bottom = 40.px).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.subtitle
                                    onDispose {}
                                }
                            },
                    )
                }
            }
        }
    }
}